import React, { useState } from 'react';
import { Plus, Pencil, Trash2, Layers3 } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  useCreateTalentDiagnosticsSection,
  useDeleteTalentDiagnosticsSection,
  useTalentDiagnosticsSections,
  useUpdateTalentDiagnosticsSection,
} from '../../../hooks/useTalentDiagnostics';

interface Props {
  definitionId: string;
}

export const TalentDiagnosticsSectionsTab: React.FC<Props> = ({ definitionId }) => {
  const { data: sections = [], isLoading } = useTalentDiagnosticsSections(definitionId);
  const createMutation = useCreateTalentDiagnosticsSection(definitionId);
  const updateMutation = useUpdateTalentDiagnosticsSection(definitionId);
  const deleteMutation = useDeleteTalentDiagnosticsSection(definitionId);

  const [form, setForm] = useState({ name: '', description: '', weighting: '0', isActive: true });
  const [editingId, setEditingId] = useState<string | null>(null);

  const resetForm = () => {
    setForm({ name: '', description: '', weighting: '0', isActive: true });
    setEditingId(null);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!definitionId) {
      toast.error('Enter a definition ID first');
      return;
    }

    if (!form.name.trim()) {
      toast.error('Section name is required');
      return;
    }

    const payload = {
      id: editingId ?? undefined,
      diagnosticDefinitionId: definitionId,
      name: form.name.trim(),
      description: form.description.trim(),
      weighting: Number(form.weighting || 0),
      isActive: form.isActive,
    };

    if (editingId) {
      updateMutation.mutate({ id: editingId, payload });
    } else {
      createMutation.mutate(payload);
    }

    resetForm();
  };

  const startEdit = (section: { id?: string; name?: string; description?: string; weighting?: number; isActive?: boolean }) => {
    setEditingId(section.id ?? null);
    setForm({
      name: section.name ?? '',
      description: section.description ?? '',
      weighting: String(section.weighting ?? 0),
      isActive: section.isActive ?? true,
    });
  };

  const handleDelete = (id?: string) => {
    if (!id) return;
    if (window.confirm('Delete this section?')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 text-left">
        <div className="flex items-center gap-2">
          <Layers3 size={18} className="text-indigo-600" />
          <h4 className="text-base font-semibold text-slate-900">Create or edit sections</h4>
        </div>
        <p className="mt-1 text-sm text-slate-500">Group diagnostics questions into logical sections for the selected definition.</p>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Section name</label>
              <input
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                placeholder="e.g. Culture"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-semibold text-slate-700">Weighting</label>
              <input
                type="number"
                value={form.weighting}
                onChange={(event) => setForm((current) => ({ ...current, weighting: event.target.value }))}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
              />
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold text-slate-700">Description</label>
            <input
              value={form.description}
              onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-slate-900 outline-none focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
              placeholder="Optional summary"
            />
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))}
            />
            Active
          </label>
          <div className="flex items-center gap-3">
            <button type="submit" className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700">
              <Plus size={16} />
              {editingId ? 'Save section' : 'Create section'}
            </button>
            {editingId ? (
              <button type="button" onClick={resetForm} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50">
                Cancel
              </button>
            ) : null}
          </div>
        </form>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 text-left">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-base font-semibold text-slate-900">Existing sections</h4>
            <p className="text-sm text-slate-500">Use these to organize your diagnostics experience.</p>
          </div>
        </div>

        {isLoading ? (
          <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 p-8 text-center text-sm text-slate-500">
            Loading sections...
          </div>
        ) : sections.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50/70 p-8 text-center text-sm text-slate-500">
            No sections found for this definition yet.
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {sections.map((section) => (
              <div key={section.id ?? `${section.name}-${section.description}`} className="flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                <div>
                  <p className="font-semibold text-slate-900">{section.name || 'Untitled section'}</p>
                  <p className="text-sm text-slate-500">{section.description || 'No description provided'} • Weighting: {section.weighting ?? 0}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => startEdit(section)} className="rounded-xl border border-slate-200 bg-white p-2 text-slate-600 transition hover:bg-slate-100">
                    <Pencil size={16} />
                  </button>
                  <button type="button" onClick={() => handleDelete(section.id)} className="rounded-xl border border-red-200 bg-red-50 p-2 text-red-600 transition hover:bg-red-100">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
